import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import type { Keg, KegStatus, KegTimelineItem } from '@/types'
import { pluralize, type PluralTemplate } from '@/lib/pluralize'

type Labels = {
  back_link: string
  since: string
  days: PluralTemplate
  download_qr: string
  passport: { qr_caption: string; public_link_hint: string }
  journey: {
    heading: string
    current_position: string
    brewery_fallback: string
    empty: string
    public_scan_title: string
    load_more: string
  }
  contact_reveal: string
  scan_located_prefix: string
  scan_unlocated: string
  view_on_map: string
}

type Props = {
  keg: Keg
  // A window over the history, newest first — never the whole thing. See KegTimeline.
  timeline: KegTimelineItem[]
  timeline_meta: { total: number; page: number }
  qr_code_svg: string
  public_url: string
  labels: Labels
}

type DayGroup = { key: string; monthKey: string; date: Date; items: KegTimelineItem[] }
type MonthGroup = { key: string; date: Date; days: DayGroup[] }

// The feed arrives newest-first, so same-day items are already adjacent: one pass, no sorting.
function groupByDay(items: KegTimelineItem[]): DayGroup[] {
  const groups: DayGroup[] = []

  for (const item of items) {
    const date = new Date(item.created_at)
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`
    const key = `${monthKey}-${date.getDate()}`
    const current = groups[groups.length - 1]

    if (current?.key === key) current.items.push(item)
    else groups.push({ key, monthKey, date, items: [item] })
  }

  return groups
}

// Days are nested inside months rather than flagged with an "opens a month" boolean, because a
// `sticky` element only sticks within its own parent: the month band has to live in a container
// that spans every day of that month, or it unsticks as soon as the first day scrolls away.
function groupByMonth(days: DayGroup[]): MonthGroup[] {
  const months: MonthGroup[] = []

  for (const day of days) {
    const current = months[months.length - 1]

    if (current?.key === day.monthKey) current.days.push(day)
    else months.push({ key: day.monthKey, date: day.date, days: [day] })
  }

  return months
}

const monthFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long' })
const weekdayFormatter = new Intl.DateTimeFormat('fr-FR', { weekday: 'short' })
const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)
// "jeu." → "Jeu" — French abbreviations carry a dot that reads as noise in a 3-letter label.
const formatWeekday = (date: Date) => capitalize(weekdayFormatter.format(date).replace('.', ''))

const DAY_MS = 86_400_000
const daysSince = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS))

// Out-in-the-wild states — the hero surfaces "how long" for these.
const OUT_STATES: KegStatus[] = ['delivered', 'in_transit', 'repair', 'lost']
// Mirrors Keg::AT_RISK_AFTER (30 days): a delivered keg out longer than this is
// money at risk — the hero flags it in flame.
const AT_RISK_DAYS = 30

// --- Icons (business vocabulary, stroke style) ---
const svg = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}
const KegGlyph = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}>
    <path d="M8.5 3h7l1 3.2c.75 2.2.75 9.4 0 11.6L15.5 21h-7l-1-3.2c-.75-2.2-.75-9.4 0-11.6L8.5 3Z" />
    <path d="M7.6 8.4h8.8M7.6 15.6h8.8" />
  </svg>
)
const TruckIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}>
    <path d="M3 6.5h10v9H3z" /><path d="M13 9.5h4l3 3v3h-7z" />
    <circle cx="7" cy="17.5" r="1.6" /><circle cx="17" cy="17.5" r="1.6" />
  </svg>
)
const HomeIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}><path d="M3 10.5 12 4l9 6.5" /><path d="M5 9.5V20h14V9.5" /><path d="M9.5 20v-5h5v5" /></svg>
)
const DropletIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}><path d="M12 3.5c3 3.7 5.5 6.6 5.5 9.7A5.5 5.5 0 0 1 6.5 13.2c0-3.1 2.5-6 5.5-9.7Z" /></svg>
)
const WarningIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} strokeWidth={1.9} className={className}>
    <path d="M12 3.5 21 19H3L12 3.5Z" /><path d="M12 10v4.5" /><circle cx="12" cy="17.4" r="0.4" fill="currentColor" stroke="none" />
  </svg>
)
const WrenchIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}><path d="M15 6.5a3.5 3.5 0 0 1-4.6 4.6l-5 5A2 2 0 0 0 8.2 19l5-5A3.5 3.5 0 0 0 18 9.4l-1.9 1.9-2.4-2.4L15 6.5Z" /></svg>
)
const SwapIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}><path d="M4 8h13l-3-3M20 16H7l3 3" /></svg>
)
const FillIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...svg} className={className}>
    <path d="M12 3v7" /><path d="m8.5 6.5 3.5 3.5 3.5-3.5" />
    <path d="M5 13h14v4.5A2.5 2.5 0 0 1 16.5 20h-9A2.5 2.5 0 0 1 5 17.5z" />
  </svg>
)

type Tone = 'ink' | 'leaf' | 'mustard' | 'flame'
const TONE_BADGE: Record<Tone, string> = {
  ink: 'bg-ink-tint text-ink',
  leaf: 'bg-leaf-tint text-leaf-deep',
  mustard: 'bg-mustard-tint text-mustard-deep',
  flame: 'bg-flame-tint text-flame-deep',
}

const STATUS_META: Record<KegStatus, { tone: Tone; Icon: typeof KegGlyph }> = {
  in_stock: { tone: 'leaf', Icon: HomeIcon },
  delivered: { tone: 'mustard', Icon: TruckIcon },
  in_transit: { tone: 'ink', Icon: TruckIcon },
  washing: { tone: 'leaf', Icon: DropletIcon },
  repair: { tone: 'flame', Icon: WrenchIcon },
  lost: { tone: 'flame', Icon: WarningIcon },
}

type EventType = 'filling' | 'delivery' | 'pickup' | 'washing' | 'incident' | 'state_change'
const EVENT_META: Record<EventType, { tone: Tone; Icon: typeof KegGlyph }> = {
  filling: { tone: 'mustard', Icon: FillIcon },
  delivery: { tone: 'mustard', Icon: TruckIcon },
  pickup: { tone: 'leaf', Icon: HomeIcon },
  washing: { tone: 'leaf', Icon: DropletIcon },
  incident: { tone: 'flame', Icon: WarningIcon },
  state_change: { tone: 'ink', Icon: SwapIcon },
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-extrabold ${TONE_BADGE[tone]}`}>
      {children}
    </span>
  )
}

function Hero({ keg, labels, daysInState }: { keg: Keg; labels: Labels; daysInState: number }) {
  const { tone, Icon } = STATUS_META[keg.status]
  const showSince = OUT_STATES.includes(keg.status) && daysInState > 0
  const atRisk = keg.status === 'delivered' && daysInState >= AT_RISK_DAYS
  // At-risk (delivered, out past the threshold) escalates the whole header to flame —
  // the "Règle de la Terre Cuite": rust/flame only signals something needing attention.
  const bandTone: Tone = atRisk ? 'flame' : tone
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-ink bg-card shadow-hard">
      {/* Filigree keg motif — personality without noise, clipped by the card. */}
      <span aria-hidden className="pointer-events-none absolute -right-8 -top-12 rotate-12 text-ink/[0.05]">
        <KegGlyph className="h-60 w-60" />
      </span>

      {/* Status band: the whole header takes the status colour. */}
      <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b-2 border-ink px-6 py-3 sm:px-8 ${TONE_BADGE[bandTone]}`}>
        <span className="inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide">
          <Icon className="h-5 w-5" />
          {keg.status_label}
        </span>
        {showSince && (
          <span className="inline-flex items-center gap-1.5 text-sm font-bold">
            {atRisk && <span aria-hidden>🔥</span>}
            {labels.since} {pluralize(daysInState, labels.days)}
          </span>
        )}
      </div>

      {/* Body: the identifier dominates, the beer + place read as an editorial line. */}
      <div className="relative px-6 py-7 sm:px-8 sm:py-9">
        <h1 className="font-display text-5xl leading-[0.92] text-ink sm:text-6xl">{keg.identifier}</h1>
        <p className="mt-4 text-lg">
          <span className="font-semibold text-ink">{keg.current_beer ?? '—'}</span>
          <span className="text-ink-soft"> · {keg.current_location ?? labels.journey.brewery_fallback}</span>
        </p>
      </div>
    </div>
  )
}

function PassportCard({
  keg,
  qrCodeSvg,
  publicUrl,
  labels,
}: {
  keg: Keg
  qrCodeSvg: string
  publicUrl: string
  labels: Labels
}) {
  return (
    <div className="rounded-2xl border-2 border-ink bg-card p-6 shadow-hard">
      <p className="text-center text-sm font-bold text-ink">{labels.passport.qr_caption}</p>
      <div
        className="mx-auto mt-4 h-44 w-44 rounded-xl border border-line bg-paper p-2 [&>svg]:h-full [&>svg]:w-full"
        dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
      />
      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block truncate text-center text-xs font-semibold text-flame hover:text-flame-deep"
        title={publicUrl}
      >
        {publicUrl.replace(/^https?:\/\//, '')}
      </a>
      <p className="mt-1 text-center text-[11px] text-ink-faint">{labels.passport.public_link_hint}</p>
      <a
        href={`/kegs/qr_codes?identifiers[]=${keg.identifier}`}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-ink bg-card px-4 py-2.5 text-sm font-bold text-ink shadow-hard-sm transition-transform hover:-translate-y-0.5 motion-reduce:transform-none"
      >
        {labels.download_qr}
      </a>
    </div>
  )
}

// The day carries the date now, so an item only needs its hour. Dropping the repeated
// "il y a 3 jours · jeu 31 juil 2026" from every row is the whole point of grouping.
const hourFormatter = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })
const formatHour = (iso: string) => hourFormatter.format(new Date(iso)).replace(':', 'h')

// An 8px dot instead of the former 44px icon badge: the operation type is already spelled out in
// the pill beside it, so the icon was decorative — and DESIGN.md reserves dots for exactly this.
const TONE_DOT: Record<Tone, string> = {
  ink: 'bg-ink-soft',
  leaf: 'bg-leaf',
  mustard: 'bg-mustard-deep',
  flame: 'bg-flame',
}

function JourneyNode({
  item,
  labels,
  isCurrent,
}: {
  item: KegTimelineItem
  labels: Labels
  isCurrent: boolean
}) {
  // A contact reveal is neutral: it says "the label is being used", not "act now". Only the report
  // and incidents earn flame — the Règle de la Terre Cuite.
  const tone: Tone =
    item.kind === 'event' ? EVENT_META[item.event_type].tone : item.kind === 'scan' ? 'flame' : 'ink'
  const place = item.kind === 'event' ? (item.location ?? labels.journey.brewery_fallback) : ''

  return (
    <li className="relative pb-4 last:pb-0">
      <span
        aria-hidden
        className={`absolute -left-[1.6rem] top-[0.45rem] h-2 w-2 rounded-full ${TONE_DOT[tone]} ${
          isCurrent ? 'ring-2 ring-flame/40 ring-offset-2 ring-offset-paper' : ''
        }`}
      />

      <div className="min-w-0">
        {item.kind === 'contact_reveal' ? (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-semibold text-ink">{labels.contact_reveal}</span>
            <span className="text-sm tabular-nums text-ink-faint">{formatHour(item.created_at)}</span>
          </div>
        ) : item.kind === 'scan' ? (
          // The public scan is the product's payoff — highlighted subtly, inside
          // the journey, with a soft flame tint rather than a shouting banner.
          <div className="rounded-xl bg-flame-tint/40 px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-extrabold text-flame-deep">{labels.journey.public_scan_title}</span>
              <span className="text-sm tabular-nums text-ink-faint">{formatHour(item.created_at)}</span>
            </div>
            <p className="mt-0.5 text-sm text-ink-soft">
              {item.latitude !== null && item.longitude !== null ? (
                <>
                  {labels.scan_located_prefix}
                  {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
                  {' · '}
                  <a
                    href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-flame hover:text-flame-deep"
                  >
                    {labels.view_on_map}
                  </a>
                </>
              ) : (
                labels.scan_unlocated
              )}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-extrabold text-ink">{place}</span>
              {isCurrent && <Pill tone="flame">{labels.journey.current_position}</Pill>}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
              <Pill tone={tone}>{item.event_type_label}</Pill>
              <span className="tabular-nums text-ink-faint">{formatHour(item.created_at)}</span>
            </div>
            <p className="mt-1 text-sm text-ink-soft">
              {item.user_email}
              {item.beer && <span className="font-semibold text-ink"> · {item.beer}</span>}
              {item.notes && <span className="text-ink-faint"> — {item.notes}</span>}
            </p>
          </>
        )}
      </div>
    </li>
  )
}

export default function KegsShow({ keg, timeline, timeline_meta, qr_code_svg, public_url, labels }: Props) {
  const [loadingMore, setLoadingMore] = useState(false)

  // timeline is newest-first. Derived, client-side value story. "Current state"
  // is driven by the newest state-changing event — a public scan is a sighting,
  // not a state change, so it never resets the clock or the current-position marker.
  const currentItem = timeline.find((i) => i.kind === 'event')
  const daysInState = currentItem ? daysSince(currentItem.created_at) : 0
  const months = groupByMonth(groupByDay(timeline))

  // Grows the window rather than paging: the journey reads top to bottom, so the previous events
  // belong under the ones already on screen, not on another page. Only the timeline props are
  // refetched; router.reload already preserves scroll and state, so the button stays under the thumb.
  const loadMore = () => {
    setLoadingMore(true)
    router.reload({
      only: ['timeline', 'timeline_meta'],
      data: { limit: timeline.length + timeline_meta.page },
      onFinish: () => setLoadingMore(false),
    })
  }

  return (
    <>
      <Head title={keg.identifier} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <Link href="/kegs" className="text-sm font-semibold text-ink-soft hover:text-ink">
          {labels.back_link}
        </Link>

        {/* Two columns on desktop: the narrative (hero + journey) flows down the
            main column while the passport rides along as a sticky sidebar, so the
            tall journey fills the space instead of leaving a gap beside the QR.
            On mobile the three stack in reading order: hero, passport, journey. */}
        <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_17rem] lg:gap-10 lg:items-start">
          <div className="animate-fade-in-up">
            <Hero keg={keg} labels={labels} daysInState={daysInState} />
          </div>

          <div className="animate-fade-in-up animation-delay-100 lg:sticky lg:top-20 lg:row-span-2">
            <PassportCard keg={keg} qrCodeSvg={qr_code_svg} publicUrl={public_url} labels={labels} />
          </div>

          <section className="animate-fade-in-up animation-delay-200 lg:mt-4">
            <h2 className="mb-6 font-display text-2xl text-ink">
              <span className="sketch">{labels.journey.heading}</span>
            </h2>
            {timeline.length === 0 ? (
              <p className="rounded-2xl border border-line bg-card px-4 py-8 text-center text-sm text-ink-soft shadow-soft">
                {labels.journey.empty}
              </p>
            ) : (
              <>
                {months.map((month) => (
                  <div key={month.key}>
                    {/* Sticks flush under the app navbar (sticky, h-16, md and up), and at the very
                        top on mobile where the nav is a fixed bottom bar instead. z-10 stays below
                        the navbar's z-30 so it slides under it rather than over. It stays put for
                        the whole month because this div — its containing block — spans every day of
                        that month. */}
                    <h3 className="sticky top-0 z-10 border-b border-line bg-paper pb-2 pt-4 font-display text-lg text-ink md:top-16">
                      {capitalize(monthFormatter.format(month.date))}
                      <span className="ml-2 align-middle text-[0.7rem] font-bold uppercase tracking-wider text-ink-faint">
                        {month.date.getFullYear()}
                      </span>
                    </h3>

                    {month.days.map((day, dayIndex) => (
                      <div
                        key={day.key}
                        className={`grid grid-cols-[3rem_1fr] gap-5 py-4 ${dayIndex === 0 ? '' : 'border-t border-line'}`}
                      >
                        <div className="pt-0.5 text-right">
                          <div className="font-display text-3xl leading-none text-mustard-deep">
                            {String(day.date.getDate()).padStart(2, '0')}
                          </div>
                          <div className="mt-1 text-[0.65rem] font-bold uppercase tracking-wider text-ink-faint">
                            {formatWeekday(day.date)}
                          </div>
                        </div>

                        {/* The dashed rule keeps the "voyage" metaphor the vertical travel line
                            carried, now doing double duty as the day's left margin. */}
                        <ol className="border-l border-dashed border-line pl-6">
                          {day.items.map((item) => (
                            <JourneyNode
                              key={`${item.kind}-${item.id}`}
                              item={item}
                              labels={labels}
                              isCurrent={item === currentItem}
                            />
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                ))}

                {timeline.length < timeline_meta.total && (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="mt-2 w-full border-t border-line pt-4 text-left text-sm font-semibold text-leaf-deep transition-colors hover:text-leaf disabled:opacity-50"
                  >
                    {labels.journey.load_more} ↓
                  </button>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  )
}
